import React, { useEffect, useState } from 'react';
import {
    Badge,
    Box,
    Icon,
    Heading,
    Stack,
    Text,
    useColorModeValue,
    IconButton,
    Tooltip
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import DataTableExtensions from 'react-data-table-component-extensions';
import 'react-data-table-component-extensions/dist/index.css';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getFacturas, reset } from '../../features/facturaVentaSlice';
import { ToastChakra } from '../../helpers/toast';
import { FiChevronLeft, FiChevronRight, FiChevronsLeft, FiChevronsRight } from 'react-icons/fi';
import { FaFileInvoice } from 'react-icons/fa';
import { customStyles } from '../../helpers/customStyles';
import { ModalAgregarFactura } from './ModalAgregarFactura';
import { ModalDetallesFactura } from './ModalDetallesFactura';
import { ModalEditarFactura } from './ModalEditarFactura';
import { AlertEliminarFactura } from './AlertEliminarFactura';
import '../../theme/solarizedTheme';
import { Loading } from '../../helpers/Loading';
import Moment from 'moment';
import { Link } from 'react-router-dom';
const Factura = ({ isRTL = false, ...props }) => {
    const direction = isRTL ? 'rtl' : 'ltr';

    const navigate = useNavigate();
    const dispatch = useDispatch();

    const themeTable = useColorModeValue('default', 'solarized');

    const { user } = useSelector((state) => state.auth);

    const { facturas, isError, isLoading, message, totalRows } = useSelector((state) => state.facturas);

    const [perPage, setPerPage] = useState(10);
    const [page, setPage] = useState(1);

    useEffect(() => {
        if (!user) {
            navigate("/login");
            return;
        }
        dispatch(getFacturas({ page: 1, perPage })); // Solo llamamos la primera vez o si cambia `perPage`

        return () => {
            dispatch(reset()); // Reseteamos al desmontar el componente
        };
    }, [user, navigate, dispatch, perPage]);

    if (isError) {
        ToastChakra('Error', message, 'error', 1500);
        console.log(message);
    }


    const handleVerFactura = (row) => {
        const estado = row.estado?.toString().trim().toUpperCase();

        if (estado !== 'ACTIVO') {
            ToastChakra(
                "Acción no permitida",
                "Solo se puede visualizar en estado ACTIVO.",
                "warning",
                4000,
                "bottom-right"
            );
            return;
           
        } else {
            navigate(`/factura_venta/pdf/${row.id}`, { state: { factura: row } });
        }
    };


    const columns = [
        {
            name: 'ID',
            selector: row => row?.id || 'N/A',
            sortable: true,
            cellExport: row => row?.id || 'N/A',
            omit: true // Esto oculta la columna
        },
        {
            name: 'NRO. FACTURA',
            selector: row => row?.numero,
            sortable: true,
            cellExport: row => row?.numero,
       
        },
        {
            name: 'FECHA EMISION',
            selector: row => Moment(row?.fechaFactura).format('DD-MM-YYYY'),
            sortable: true,
            cellExport: row => Moment(row?.fechaFactura).format('DD-MM-YYYY'),
       
        },
        {
            name: 'CLIENTE',
            selector: row => row?.cliente,
            sortable: true,
            cellExport: row => row?.cliente,
       
        },
        {
            name: 'DNI/RUC',
            selector: row => row?.dni,
            sortable: true,
            cellExport: row => row?.dni,

        },
        {
            name: 'MONTO',
            selector: row => row?.totalGeneral,
            sortable: true,
            cellExport: row =>
              new Intl.NumberFormat('es-ES', {
                style: 'currency',
                currency: 'EUR',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
                useGrouping: true
              }).format(row?.totalGeneral),
            cell: row => (

              <div>
                    <Badge 
                        bg="blue.600"
                        variant="solid"
                        px={0} // Eliminar padding horizontal para controlar con `w`
                        py={2}
                        textAlign="center"
                        rounded="full"
                        color="white"
                        w="100px" // 🔹 Ancho fijo
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        >
                        {new Intl.NumberFormat('es-ES', {
                            style: 'currency',
                            currency: 'EUR',
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                            useGrouping: true
                        }).format(row?.totalGeneral)}
                    </Badge>

              </div>
            ),
          },
  
        {
            name: 'ESTADO',
            selector: row => { return row.estado  },
            sortable: true,
            cellExport: row => row.estado,
            center: true,
            cell: row => (
                <div>
                    <Badge
                        colorScheme={   
                            row.estado === 'ACTIVO'
                                ? 'green'
                                : row.estado === 'ANULADO'
                                ? 'red'
                                : row.estado === 'PENDIENTE'
                                ? 'orange'
                                : 'gray' // Color por defecto si no coincide con ninguno
                        }
                        variant="solid"
                        w={24}
                        textAlign="center"
                        py={2}
                        rounded="full"
                    >
                        {row.estado }
                    </Badge>
                </div>
            )
        },
        {
            name: 'ACCIONES',
            export: false,
            center: true,
            cell: row => (
                <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
                    <Link to={`/factura_venta/pdf/${row.id}`}>
                        <Tooltip hasArrow label='Ver Factura de Venta' placement='auto'>
                            <IconButton
                                aria-label="Ver"
                                icon={<FaFileInvoice />}
                                fontSize="lg"
                                colorScheme="yellow"
                                variant="ghost"
                                onClick={() => handleVerFactura(row)}
                                isDisabled={row.estado?.toString().trim().toUpperCase() !== 'ACTIVO'}
                            />
                        </Tooltip>
                    </Link>
                    <ModalDetallesFactura row={row} page={page} perPage={perPage} />
                    <ModalEditarFactura row={row} page={page} perPage={perPage} />
                    <AlertEliminarFactura row={row} page={page} perPage={perPage} />
                </Box>
            ),
            width: '220px'
        }
    ]

    const handlePageChange = (newPage) => {
        setPage(newPage); // Actualiza el estado local de la página
        dispatch(getFacturas({ page: newPage, perPage })); // Despacha la acción con la nueva página
    };

    const handleRowsPerPageChange = (newPerPage) => {
        setPerPage(newPerPage); // Cambiamos el número de filas por página
        dispatch(getFacturas({ page: 1, perPage: newPerPage })); // Reiniciamos a la primera página
    };

    const tableData = {
        columns: columns,
        data: facturas,
    }

    if(isLoading) {
        return <Loading />
    }

    return (
        <div style={{ direction }} {...props}>

            <Stack spacing={4} direction="row" justifyContent="space-between" py={4} px={4}>
                <Heading size="lg">Factura Venta</Heading>
                <ModalAgregarFactura page={page} perPage={perPage} />
            </Stack>
            <Box
                overflow="hidden"
                boxShadow={'base'}
                bg="white"
                _dark={{ bg: "primary.1000" }}
                mt={2}
                pt={2}
                rounded="2xl"
            >
                <DataTableExtensions
                    {...tableData}
                    print={false}
                    exportHeaders={true}
                    filterPlaceholder="BUSCAR"
                    numberOfColumns={columns.length}
                    fileName={'FACTURAS_' + new Date().toLocaleDateString()}
                >
                    <DataTable
                        defaultSortField="createdAt"
                        defaultSortAsc={false}
                        defaultSortOrder="desc"
                        pagination
                        paginationIconFirstPage={<Icon as={FiChevronsLeft} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconLastPage={<Icon as={FiChevronsRight} boxSize={6} _dark={{ color: "gray.300" }} />}
                        paginationIconPrevious={<Icon as={FiChevronLeft} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationIconNext={<Icon as={FiChevronRight} boxSize={6} _dark={{ color: "gray.300", _hover: { color: "white" } }} />}
                        paginationServer
                        paginationPerPage={perPage}
                        onChangeRowsPerPage={handleRowsPerPageChange}
                        paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30, 50]}
                        paginationDefaultPage={page}
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        paginationComponentOptions={{
                            rowsPerPageText: 'Filas por página:',
                            rangeSeparatorText: 'de',
                            noRowsPerPage: false,
                            selectAllRowsItem: true,
                            selectAllRowsItemText: 'Todos',
                        }}
                        theme={themeTable}
                        customStyles={customStyles}
                        pointerOnHover={true}
                        responsive={true}
                        noDataComponent={<Text mb={4} fontSize="lg">NO SE ENCONTRARON DATOS</Text>}
                    />
                </DataTableExtensions>
            </Box>
       </div> 
    );
}

export default Factura;